import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api, APIError } from "~/lib/api";
import { Mark } from "~/components/Brand/Mark";
import { Button } from "~/components/ui/Button";

export const Route = createFileRoute("/_public/login")({
  component: LoginPage,
});

type Stage = "email" | "code" | "password";

function LoginPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function requestLink(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.requestMagicLink(email);
      setStage("code");
    } catch {
      setError("Couldn't send the code. Try again.");
    } finally { setBusy(false); }
  }

  async function verifyLink(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.verifyMagicLink(code.trim());
      navigate({ to: "/" });
    } catch (e) {
      if (e instanceof APIError && e.status === 401) {
        setError("That code didn't work. It may have expired.");
      } else {
        setError("Couldn't sign you in. Try again.");
      }
    } finally { setBusy(false); }
  }

  async function passwordLogin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.login(email, password);
      navigate({ to: "/" });
    } catch (e) {
      if (e instanceof APIError && e.status === 401) {
        setError("Email or password is incorrect.");
      } else {
        setError("Couldn't sign you in. Try again.");
      }
    } finally { setBusy(false); }
  }

  return (
    <main
      className="min-h-screen flex flex-col"
      style={{ background: "var(--bg-system)" }}
    >
      <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto px-6 py-12">
        <div className="flex flex-col items-center text-center">
          <Mark size={72} />
          <h1 className="text-largeTitle mt-6" style={{ color: "var(--label-primary)" }}>
            SquaredIoT
          </h1>
          <p className="text-body mt-2" style={{ color: "var(--label-secondary)" }}>
            Sign in to control your devices.
          </p>
        </div>

        <div className="mt-10">
          <AnimatePresence mode="wait">
            {stage === "email" && (
              <motion.form
                key="email"
                onSubmit={requestLink}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="space-y-3"
              >
                <Field
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button
                  type="submit"
                  size="lg"
                  loading={busy}
                  disabled={!email}
                  trailingIcon={<ArrowRight size={16} />}
                  className="w-full"
                >
                  Continue
                </Button>
                <button
                  type="button"
                  onClick={() => setStage("password")}
                  className="w-full text-callout pt-1 transition-opacity active:opacity-60"
                  style={{ color: "var(--system-blue)" }}
                >
                  Use a password instead
                </button>
              </motion.form>
            )}

            {stage === "code" && (
              <motion.form
                key="code"
                onSubmit={verifyLink}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="space-y-3"
              >
                <p className="text-footnote text-center pb-1" style={{ color: "var(--label-secondary)" }}>
                  We sent a 6-digit code to <span style={{ color: "var(--label-primary)" }}>{email}</span>.
                </p>
                <Field
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  style={{ fontFamily: "var(--font-mono)", textAlign: "center", letterSpacing: "0.4em", fontSize: 20 }}
                />
                <Button type="submit" size="lg" loading={busy} disabled={code.trim().length < 4} className="w-full">
                  Sign in
                </Button>
                <button
                  type="button"
                  onClick={() => { setStage("email"); setCode(""); setError(null); }}
                  className="w-full text-callout pt-1 transition-opacity active:opacity-60"
                  style={{ color: "var(--system-blue)" }}
                >
                  Use a different email
                </button>
              </motion.form>
            )}

            {stage === "password" && (
              <motion.form
                key="password"
                onSubmit={passwordLogin}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18 }}
                className="space-y-3"
              >
                <Field
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Field
                  type="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button type="submit" size="lg" loading={busy} disabled={!email || !password} className="w-full">
                  Sign in
                </Button>
                <button
                  type="button"
                  onClick={() => { setStage("email"); setPassword(""); setError(null); }}
                  className="w-full text-callout pt-1 transition-opacity active:opacity-60"
                  style={{ color: "var(--system-blue)" }}
                >
                  Email me a code instead
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                className="mt-3 px-3 py-2 text-footnote text-center"
                style={{
                  borderRadius: 10,
                  color: "var(--system-red)",
                  background: "color-mix(in oklab, var(--system-red) 10%, transparent)",
                }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}

type FieldProps = React.InputHTMLAttributes<HTMLInputElement>;
function Field(props: FieldProps) {
  const { className = "", style, ...rest } = props;
  return (
    <input
      {...rest}
      className={`w-full text-body focus:outline-none transition-colors ${className}`}
      style={{
        background: "var(--fill-tertiary)",
        border: "0.5px solid var(--separator)",
        color: "var(--label-primary)",
        height: 50,
        paddingInline: 16,
        borderRadius: 12,
        ...style,
      }}
    />
  );
}
